import { Modal, Typography } from 'antd';
import { i18n } from 'src/i18n';

interface VotingBulkSummaryEntry {
  companyName?: string;
  isin?: string;
  meetingId?: string;
}

export interface VotingBulkSummary {
  created: VotingBulkSummaryEntry[];
  duplicates: VotingBulkSummaryEntry[];
  unmatched: VotingBulkSummaryEntry[];
}

const formatEntry = (entry: VotingBulkSummaryEntry) => {
  const company = [entry.companyName, entry.isin && `(${entry.isin})`]
    .filter(Boolean)
    .join(' ');

  return entry.meetingId
    ? `${company} · ${i18n('entities.voting.bulkSummary.meeting', entry.meetingId)}`
    : company;
};

const SummarySection = ({ title, hint, entries }: { title: string; hint: string; entries: VotingBulkSummaryEntry[] }) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography.Text strong>{`${title} (${entries.length})`}</Typography.Text>
      <br />
      <Typography.Text type="secondary">{hint}</Typography.Text>
      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
        {entries.map((entry, idx) => (
          <li key={idx}>{formatEntry(entry)}</li>
        ))}
      </ul>
    </div>
  );
};

export const showVotingBulkSummary = (summary: VotingBulkSummary) => {
  const { created, duplicates, unmatched } = summary;
  const hasSkipped = duplicates.length > 0 || unmatched.length > 0;

  const modal = created.length === 0
    ? Modal.error
    : hasSkipped
      ? Modal.warning
      : Modal.success;

  const title = created.length === 0
    ? i18n('entities.voting.bulkSummary.titleNone')
    : hasSkipped
      ? i18n('entities.voting.bulkSummary.titlePartial', created.length)
      : i18n('entities.voting.bulkSummary.titleAll', created.length);

  modal({
    title,
    width: 640,
    content: (
      <>
        <Typography.Paragraph style={{ marginTop: 12, marginBottom: 8 }}>
          {i18n(
            'entities.voting.bulkSummary.counts',
            created.length,
            unmatched.length,
            duplicates.length,
          )}
        </Typography.Paragraph>
        <div
          style={{
            maxHeight: '50vh',
            overflowY: 'auto',
            overflowWrap: 'anywhere',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 6,
            padding: '12px 12px 0',
          }}
        >
          <SummarySection
            title={i18n('entities.voting.bulkSummary.created')}
            hint={i18n('entities.voting.bulkSummary.createdHint')}
            entries={created}
          />
          <SummarySection
            title={i18n('entities.voting.bulkSummary.unmatched')}
            hint={i18n('entities.voting.bulkSummary.unmatchedHint')}
            entries={unmatched}
          />
          <SummarySection
            title={i18n('entities.voting.bulkSummary.duplicates')}
            hint={i18n('entities.voting.bulkSummary.duplicatesHint')}
            entries={duplicates}
          />
        </div>
      </>
    ),
  });
};
